/**
 * The bill.
 *
 * A proposal asks somebody to buy; an invoice asks them to pay. Roof Board has
 * had the first since ADR 0032 and, until now, nothing at all of the second —
 * the only invoice it knew about was the one it pushes into QuickBooks when a
 * rep presses Sends (ADR 0042), which nobody outside the accounts ever sees.
 *
 * Jonathon, 2026-08-27: "vendors ask for pdf invoices that we can send that
 * will trigger them to pay us." So this is a document, not an integration. It
 * is rendered to a PDF in the browser (`apps/web/src/lib/invoicePdf.ts`),
 * emailed from the job like anything else, and it never talks to QuickBooks.
 *
 * ## The total is the contract, and that is enforced here
 *
 * The lines on an invoice are the estimate's own line items — but an estimate's
 * lines do not add up to what the customer owes. Discounts come off, change
 * orders go on, and a commercial job bills a general contractor's fee on top.
 * `contractAmount` in ladder.ts is the one definition of what is owed
 * (CLAUDE.md rule 1), so this file builds the lines *out of the same parts that
 * function reads* and the test pins `invoiceTotal(lines) === grandTotal`.
 *
 * That is the whole reason the discount and the fee are lines rather than a
 * footer: a bill whose lines do not add to its total is a bill somebody
 * disputes, and one built from `customer_price` alone would silently overcharge
 * every discounted job in the pipeline.
 *
 * ## What it does not do
 *
 * No tax, ever — Jonathon, 2026-08-27, asked directly. No partial or progress
 * billing: this invoices an estimate, all of it. Billing a deposit or a stage
 * is the payment schedule's job and would be a different document.
 *
 * ## More than one estimate
 *
 * What it does bill, from 2026-09-22, is more than one of them at a time. A job
 * priced three ways carries three estimates, and a customer can sign for more
 * than one of them — Katie Harding-Smith signed all four options on job 1015.
 * `invoiceGroupLines` puts them on one bill, each keeping its own discount and
 * its own general contractor fee, because those are per-estimate rules and a
 * fee taken once over the sum is not the fee the job carries.
 *
 * ## What has been paid
 *
 * The snapshot carries `received` and `balanceDue`, and neither is worked out
 * here. Both come off `jobBalance` in balance.ts, which is the only thing in
 * Roof Board allowed to answer how much is left (CLAUDE.md rule 5).
 */

import { gcFee, isExpenseRemoved, overriddenCents } from "./autoExpenses.js";
import { contractAmount } from "./ladder.js";
import { addCents, cents, toCents, type Cents } from "./money.js";

/**
 * How long a customer has to pay.
 *
 * Two days. Jonathon, 2026-08-27, asked what the terms should be: "due date are
 * 2 days out". Short on purpose — these go to vendors and carriers who pay off
 * a document, not to homeowners on terms.
 */
export const INVOICE_DUE_DAYS = 2;

/** One row on the bill. `amount` is what is charged; the rest explains it. */
export interface InvoiceLine {
  description: string;
  qty: number;
  /** Per-unit, for the column that shows it. Derived, never the source. */
  unitPrice: Cents;
  /** What this line adds to the total. The authority. */
  amount: Cents;
  /**
   * Which estimate this line came off, when the bill covers more than one.
   *
   * A heading on the document rather than a line of its own — a heading with
   * an amount would either be double-counted or read as a charge of nothing.
   * Absent on a bill for a single estimate, which is most of them.
   */
  group?: string;
}

/** Either side of the bill: who is sending it, who owes it. */
export interface InvoiceParty {
  name: string;
  email: string | null;
  /** Street, then "City, ST ZIP", then anything else. Already split. */
  address: readonly string[];
}

/**
 * Who the bill goes to instead of the homeowner, field by field. Any field
 * left out, or given as `undefined`, falls back to the homeowner's.
 */
export type InvoicePartyOverride = {
  [K in keyof InvoiceParty]?: InvoiceParty[K] | undefined;
};

/** A whole invoice, as it is rendered and as it is stored. */
export interface InvoiceSnapshot {
  number: string;
  /** ISO dates, `YYYY-MM-DD`. */
  invoiceDate: string;
  dueDate: string;
  from: InvoiceParty;
  billTo: InvoiceParty;
  /** Free text under the dates. Carries the customer's name when billing a
   * third party — which is exactly what the invoice Jonathon sent as the
   * example did, billing Alfa and naming Bennett McGehee in the memo. */
  memo: string;
  lines: readonly InvoiceLine[];
  total: Cents;
  /**
   * What the customer has already paid on this job, the day the bill was made.
   *
   * Passed in, never worked out here. `jobBalance` in balance.ts is the one
   * answer to what has arrived and what is left (CLAUDE.md rule 5), and a
   * document that subtracted for itself would be the second answer that
   * Jonathon asked on 2026-09-08 never to have again.
   *
   * Absent on every invoice made before 2026-09-22, which is why it is
   * optional: a frozen snapshot is never rewritten, and the PDF draws what the
   * snapshot has.
   */
  received?: Cents;
  /**
   * What is still owed on the job — `jobBalance().outstanding`, as it is.
   *
   * On the ordinary bill, which covers the whole contract, this is the total
   * less what has been received. It is deliberately NOT that subtraction: when
   * the bill covers one option of several, the job can owe more than this
   * invoice asks for, and the figure a customer reads has to be the debt
   * rather than a flattering part of it.
   */
  balanceDue?: Cents;
}

/**
 * What the invoice is called, and why it is the job number.
 *
 * Jonathon, 2026-08-27: "ideally the same number that would be going to
 * quickbooks." That number is the job number — `buildInvoice` in
 * `supabase/functions/quickbooks/payload.ts` sets `DocNumber` to it — so a
 * payment landing in the accounts can be matched to the bill that asked for it
 * without anybody keeping a second list.
 *
 * A second invoice on the same job takes a suffix, because QuickBooks rejects a
 * duplicate `DocNumber` outright and two bills reading "4014911" is a dispute
 * waiting to happen.
 */
export function invoiceNumber(
  jobNumber: number | string,
  sequence = 1,
): string {
  const base = String(jobNumber).trim() || "INV";
  return sequence <= 1 ? base : `${base}-${sequence}`;
}

const DAY = 24 * 60 * 60 * 1000;

/**
 * The day it falls due: the invoice date plus {@link INVOICE_DUE_DAYS}.
 *
 * Parsed as UTC midnight, like every other date arithmetic in this package. A
 * local-time parse makes the due date depend on the reader's timezone, and a
 * bill does not fall due an hour earlier in Denver.
 */
export function invoiceDueDate(
  invoiceDate: string,
  days = INVOICE_DUE_DAYS,
): string {
  const from = Date.parse(`${invoiceDate}T00:00:00Z`);
  if (Number.isNaN(from)) return invoiceDate;
  return new Date(from + days * DAY).toISOString().slice(0, 10);
}

/** The parts of an estimate line the bill needs. */
export interface InvoiceableItem {
  description?: string | null;
  qty?: unknown;
  unit?: string | null;
  customer_price?: unknown;
  discount?: unknown;
  work_doing?: boolean | null;
  customer_visible?: boolean | null;
  sort_order?: number | null;
}

/** The parts of a change order the bill needs. */
export interface InvoiceableChangeOrder {
  description?: string | null;
  qty?: unknown;
  unit_price?: unknown;
  amount?: unknown;
  sort_order?: number | null;
}

export interface InvoiceLinesInput {
  items: readonly InvoiceableItem[];
  changeOrders?: readonly InvoiceableChangeOrder[];
  /** `estimates.office_discount`, in whatever the database handed over. */
  officeDiscount?: unknown;
  /** 'commercial' bills a general contractor's fee. Nothing else does. */
  jobType?: string | null;
  /**
   * A GC fee somebody typed over the calculated one, and whether the fee was
   * struck off this job altogether.
   *
   * The bill has to agree with the estimate. The fee is billed on at cost, so
   * the figure the job carries is the figure the customer pays — and once a
   * person can correct or remove that figure, an invoice recomputing it from
   * the rule would quietly bill something nobody agreed to. Same two inputs
   * `contractAmount` takes, for exactly that reason.
   */
  gcFeeOverride?: unknown;
  gcFeeRemoved?: unknown;
}

/** What a line with no description of its own is called on the bill. */
const UNNAMED_LINE = "Roofing work";

/** The one line that is not work: everything taken off, added together. */
export const INVOICE_DISCOUNT_LINE = "Discount";

/** The commercial fee, billed on. See `gcFee` in autoExpenses.ts. */
export const INVOICE_GC_FEE_LINE = "General contractor fee";

/**
 * The lines of the bill, in the order they are read.
 *
 * Work first, in the estimate's own order, then change orders, then one
 * discount line, then the fee. A line the estimate is not doing is not on the
 * bill; neither is one hidden from the customer, because a bill is the last
 * place to surprise somebody with wording they were never shown.
 *
 * Zero-priced lines stay. They are the warranty and scope wording a package
 * carries, and a customer comparing the bill to what they signed should find
 * the same list.
 */
export function invoiceLines(input: InvoiceLinesInput): InvoiceLine[] {
  const doing = input.items.filter(
    (item) => item.work_doing !== false && item.customer_visible !== false,
  );

  const lines: InvoiceLine[] = doing.map((item) => {
    const amount = toCents(item.customer_price);
    const qty = Number(item.qty ?? 1);
    return {
      description: (item.description ?? "").trim() || UNNAMED_LINE,
      qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
      unitPrice: unitPriceOf(amount, qty),
      amount,
    };
  });

  for (const order of input.changeOrders ?? []) {
    const amount = toCents(order.amount);
    const qty = Number(order.qty ?? 1);
    lines.push({
      description: (order.description ?? "").trim() || UNNAMED_LINE,
      qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
      unitPrice: toCents(order.unit_price) || unitPriceOf(amount, qty),
      amount,
    });
  }

  // Every reduction as one line. Two discount columns on a customer document
  // invite the question "which one applied?", and the answer never fits.
  const discount = addCents(
    ...doing.map((item) => toCents(item.discount)),
    toCents(input.officeDiscount),
  );
  if (discount !== 0) {
    const amount = cents(-discount);
    lines.push({
      description: INVOICE_DISCOUNT_LINE,
      qty: 1,
      unitPrice: amount,
      amount,
    });
  }

  // The fee is billed on the contract before it — the same base
  // `contractAmount` uses, which is why it is taken after the discount line.
  const base = addCents(...lines.map((line) => line.amount));
  const fee = isExpenseRemoved(input.gcFeeRemoved)
    ? 0
    : overriddenCents(input.gcFeeOverride, gcFee(base, input.jobType));
  if (fee !== 0) {
    lines.push({
      description: INVOICE_GC_FEE_LINE,
      qty: 1,
      unitPrice: fee,
      amount: fee,
    });
  }

  return lines;
}

/** One estimate's worth of a bill, and what the document calls it. */
export interface InvoiceGroup extends InvoiceLinesInput {
  /**
   * The estimate's name, as the builder's own tabs read it
   * (`estimateOptionLabel`). Printed above its lines when there is more than
   * one; ignored when there is only one, because a bill for the only thing
   * being sold does not need a heading saying so.
   */
  label?: string | null;
}

/**
 * The lines of a bill that covers several estimates.
 *
 * One job can be sold more than once — Katie Harding-Smith signed for all four
 * options on job 1015 — and until now billing that took four invoices, because
 * the modal could only see the estimate whose tab was open.
 *
 * Each estimate keeps its own arithmetic. Its lines, its change orders, its
 * discount and its general contractor fee are worked out by `invoiceLines`
 * exactly as they would be on a bill of its own, and the groups are then read
 * one after another. That is not a tidiness choice: the fee has a $500 floor
 * (`gcFee`), so a fee taken once over the combined total is a different number
 * from the fees the job's own estimates carry, and the bill would stop
 * agreeing with the contract. Two estimates, two fees — the same figure
 * `invoiceAmount` adds up when it works out what the customer owes.
 */
export function invoiceGroupLines(
  groups: readonly InvoiceGroup[],
): InvoiceLine[] {
  const named = groups.length > 1;
  return groups.flatMap((group) => {
    const label = (group.label ?? "").trim();
    const lines = invoiceLines(group);
    return named && label
      ? lines.map((line) => ({ ...line, group: label }))
      : lines;
  });
}

/**
 * The same total as `invoiceGroupLines`, the way the ladder works it out.
 *
 * The group-by-group mirror of `invoiceContractTotal`, and pinned against it
 * for the same reason: if the two ever disagree the bill is wrong and the
 * ladder is right.
 */
export function invoiceGroupContractTotal(
  groups: readonly InvoiceGroup[],
): Cents {
  return addCents(...groups.map((group) => invoiceContractTotal(group)));
}

/**
 * A unit price to print, worked back from the line total.
 *
 * `estimate_items.customer_price` is the LINE total, not a unit price — the
 * trap named in CLAUDE.md — so the column the sample invoice shows has to be
 * derived, and the derived figure is the one that can be a cent out on an
 * awkward quantity. The line total stays authoritative; only the middle column
 * rounds.
 */
function unitPriceOf(amount: Cents, qty: number): Cents {
  if (!Number.isFinite(qty) || qty <= 0) return amount;
  return cents(amount / qty);
}

/** What the bill adds up to. The lines are the arithmetic, not a footer. */
export function invoiceTotal(lines: readonly InvoiceLine[]): Cents {
  return addCents(...lines.map((line) => line.amount));
}

/**
 * The same total, from the same parts, the way the ladder works it out.
 *
 * Exported so a test can hold the two against each other rather than trusting
 * that `invoiceLines` kept up with `contractAmount`. If these ever disagree,
 * the bill is wrong and the ladder is right — see CLAUDE.md rule 1.
 */
export function invoiceContractTotal(input: InvoiceLinesInput): Cents {
  const doing = input.items.filter(
    (item) => item.work_doing !== false && item.customer_visible !== false,
  );
  return contractAmount({
    lineTotal: addCents(...doing.map((item) => toCents(item.customer_price))),
    lineDiscount: addCents(...doing.map((item) => toCents(item.discount))),
    changeOrders: addCents(
      ...(input.changeOrders ?? []).map((order) => toCents(order.amount)),
    ),
    officeDiscount: toCents(input.officeDiscount),
    jobType: input.jobType,
    gcFeeOverride: input.gcFeeOverride,
    gcFeeRemoved: input.gcFeeRemoved,
  }).grandTotal;
}

/** The company's own block, off `company_settings`. */
export interface InvoiceCompany {
  name?: string | null;
  email?: string | null;
  address?: string | null;
}

/** Enough of the customer to bill them. */
export interface InvoiceCustomer {
  full_name?: string | null;
  company?: string | null;
  email?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

/**
 * An address as lines, from the loose fields the database keeps it in.
 *
 * "United States" is appended because the sample invoice carries it and a
 * carrier's document scanner expects a country; nothing here is sent abroad.
 */
export function invoiceAddressLines(input: {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}): string[] {
  const street = (input.address ?? "").trim();
  const town = [
    (input.city ?? "").trim(),
    [(input.state ?? "").trim(), (input.zip ?? "").trim()]
      .filter(Boolean)
      .join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const lines = [street, town].filter(Boolean);
  return lines.length ? [...lines, "United States"] : [];
}

/**
 * The company's address, which is one text field rather than four.
 *
 * `company_settings.address` is typed by hand in Tools, so it arrives as one
 * string with commas or newlines in it. Split rather than printed whole: a
 * single long line under the company name is the one thing that makes a
 * generated invoice look generated.
 *
 * Newlines if it has them; otherwise the street and everything after it, which
 * is where the one comma that matters is. Splitting on every comma is what the
 * first version did, and it printed "Pelham" and "AL 35124" on separate lines —
 * an address nobody writes that way.
 */
export function companyAddressLines(address?: string | null): string[] {
  const text = (address ?? "").trim();
  if (!text) return [];

  const typed = text
    .split(/\r?\n/)
    .map((line) => line.trim().replace(/,$/, ""))
    .filter(Boolean);
  if (typed.length > 1) return [...typed, "United States"];

  const comma = text.indexOf(",");
  const parts =
    comma === -1
      ? [text]
      : [text.slice(0, comma).trim(), text.slice(comma + 1).trim()];
  return [...parts.filter(Boolean), "United States"];
}

export interface BuildInvoiceInput extends Partial<InvoiceLinesInput> {
  /**
   * The estimates being billed, when the bill covers more than the one whose
   * tab is open. Takes the place of `items` and `changeOrders` when it is set.
   */
  groups?: readonly InvoiceGroup[];
  /** `jobBalance().collected` — what has arrived. Never computed here. */
  received?: Cents | null;
  /** `jobBalance().outstanding` — what is still owed. Never computed here. */
  balanceDue?: Cents | null;
  number: string;
  invoiceDate: string;
  dueDate?: string;
  company: InvoiceCompany;
  /** The homeowner. Who the bill goes to unless somebody says otherwise. */
  customer: InvoiceCustomer;
  /** Set when the bill goes to a carrier, a GC or a property manager. */
  billTo?: InvoicePartyOverride | null | undefined;
  memo?: string | null;
}

/**
 * The name a customer is billed under: the business if there is one.
 *
 * Empty when there is nothing to go on, rather than a stand-in. An invoice
 * addressed to "Customer" is worse than one that refuses to be created —
 * `invoiceBlocker` catches the empty case, and somebody fixes the contact.
 */
export function invoiceCustomerName(customer: InvoiceCustomer): string {
  const person = (customer.full_name ?? "").trim();
  const business = (customer.company ?? "").trim();
  if (business && person) return `${business} — ${person}`;
  return business || person || "";
}

/**
 * The whole document, ready to render and ready to freeze.
 *
 * Everything it needs is passed in. Nothing is fetched, nothing is defaulted
 * from a clock: the caller supplies the date, so the same inputs always produce
 * the same bill and a test can say so.
 */
export function buildInvoice(input: BuildInvoiceInput): InvoiceSnapshot {
  const lines = input.groups?.length
    ? invoiceGroupLines(input.groups)
    : invoiceLines({ ...input, items: input.items ?? [] });
  const customerName = invoiceCustomerName(input.customer);
  const billTo: InvoiceParty = {
    name: (input.billTo?.name ?? "").trim() || customerName,
    email:
      (input.billTo?.email ?? "").trim() ||
      (input.customer.email ?? "").trim() ||
      null,
    address: input.billTo?.address ?? invoiceAddressLines(input.customer),
  };

  // Name the customer when the bill is going to somebody else. A carrier pays
  // off a document and files it against a claim; without the homeowner's name
  // on it, the payment arrives with nothing to match it to.
  const named = customerName !== "" && billTo.name.trim() !== customerName;
  const memo =
    (input.memo ?? "").trim() || (named ? `Customer: ${customerName}` : "");

  return {
    number: input.number,
    invoiceDate: input.invoiceDate,
    dueDate: input.dueDate ?? invoiceDueDate(input.invoiceDate),
    from: {
      name: (input.company.name ?? "").trim() || "",
      email: (input.company.email ?? "").trim() || null,
      address: companyAddressLines(input.company.address),
    },
    billTo,
    memo,
    lines,
    total: invoiceTotal(lines),
    // Only where the caller knows. A screen that has not read the balance says
    // nothing about it rather than printing a confident $0.00 received.
    ...(input.received == null ? {} : { received: cents(input.received) }),
    ...(input.balanceDue == null
      ? {}
      : { balanceDue: cents(input.balanceDue) }),
  };
}

/** `$2,424.00`, the way an invoice writes money. */
export function invoiceMoney(value: Cents): string {
  const negative = value < 0;
  const whole = Math.abs(value);
  const dollars = Math.floor(whole / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const remainder = (whole % 100).toString().padStart(2, "0");
  return `${negative ? "-" : ""}$${dollars}.${remainder}`;
}

/** `08/27/2026`, from an ISO date. The format on the sample invoice. */
export function invoiceDate(iso: string): string {
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
}

/**
 * The quantity column: whole numbers stay whole, fractions keep two places.
 *
 * Squares are genuinely fractional (28.33 SQ) and a quantity printed as
 * "28.330000000000002" is how a customer decides the document is not to be
 * trusted.
 */
export function invoiceQty(qty: number, unit?: string | null): string {
  if (!Number.isFinite(qty)) return "1";
  const rounded = Math.round(qty * 100) / 100;
  const text = Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toFixed(2).replace(/0$/, "");
  const suffix = (unit ?? "").trim();
  return suffix ? `${text} ${suffix}` : text;
}

/** Named the way a person filing it would name it. */
export function invoiceFilename(number: string): string {
  const safe = number.replace(/[^A-Za-z0-9._-]/g, "-");
  return `invoice-${safe}.pdf`;
}

/**
 * Nothing to bill.
 *
 * A zero invoice is not a bill, it is a mistake somebody is about to email a
 * carrier. Callers check this before creating one; the wording is here so the
 * modal and any future caller say the same thing.
 */
export function invoiceBlocker(snapshot: InvoiceSnapshot): string | null {
  if (snapshot.lines.length === 0) {
    return "This estimate has no line items to bill.";
  }
  if (snapshot.total <= 0) {
    return "This estimate adds up to nothing — there is no invoice to send.";
  }
  if (!snapshot.billTo.name.trim()) {
    return "An invoice needs somebody to bill.";
  }
  return null;
}
